/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

/**
 * Dynamically import any plugin belonging to this package
 *
 * The resolution of imports during bundling is handled by @rollup/plugin-dynamic-import-vars
 * which makes some restrictions on the use of aliases, subpaths and file extensions
 * @see https://www.npmjs.com/package/@rollup/plugin-dynamic-import-vars
 *
 * @param {String} moduleName - the identifier that tells what module is to be imported
 * @returns {Promise} resolves with the imported module
 */
async function importPlugin(moduleName) {
    let foundModule;

    const isMissingPluginError = error =>
        /Unknown variable dynamic import|Cannot find module|Failed to fetch dynamically imported module/i.test(
            String(error?.message || error)
        );

    const subdirMatches = moduleName.match(/^taoQtiNuiPreviewer\/runner\/plugins\/([a-zA-Z0-9/]+)\/plugin$/);

    if (subdirMatches) {
        try {
            const subdirs = subdirMatches[1].split('/');
            if (subdirs.length === 1) {
                foundModule = await import(`./runner/plugins/${subdirs[0]}/plugin.js`);
            }
        } catch (err) {
            if (!isMissingPluginError(err)) {
                throw err;
            }
            console.error(`Plugin "${moduleName}" not found:`, err);
        }
    }

    return foundModule || (await import('./runner/plugins/dummyPlugin.js'));
}

/**
 * Dynamically import certain modules of this package
 *
 * @param {String} moduleName - the identifier that tells what module is to be imported
 * @returns {Promise} resolves with the imported module
 */
export function importModule(moduleName) {
    if (moduleName.startsWith('taoQtiNuiPreviewer/runner/plugins/')) {
        return importPlugin(moduleName);
    }

    switch (moduleName) {
        case 'taoQtiNuiPreviewer/runner/qtiPreviewer':
            return import('./runner/qtiPreviewer.js');

        case 'taoQtiNuiPreviewer/runner/proxy/previewerProxy':
            return import('./runner/proxy/previewerProxy.js');

        case 'taoQtiNuiPreviewer/runner/proxy/constructPreviewerProxy':
            return import('./runner/proxy/constructPreviewerProxy.js');
    }

    return Promise.reject(new Error(`Module "${moduleName}" not found`));
}
